import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Flag, FlagOff } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable Flag/Unflag button.
 * Props:
 *  - resource: "sales" | "proformas" | "invoices" | "receipts"
 *  - id: record id
 *  - flag: current flag object on the record (or null/undefined if unflagged)
 *  - onChanged: callback fired after successful flag/unflag
 *  - testId: override for the trigger's data-testid
 */
export function FlagButton({ resource, id, flag, onChanged, testId }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const isFlagged = !!flag?.flagged;

  const doFlag = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/${resource}/${id}/flag`, { note: note.trim() });
      toast.success("Flagged for follow-up");
      setOpen(false); setNote("");
      onChanged?.(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const doUnflag = async () => {
    if (!window.confirm("Remove flag from this record?")) return;
    setBusy(true);
    try {
      await api.post(`/${resource}/${id}/unflag`);
      toast.success("Flag removed");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  if (isFlagged) {
    return (
      <Button
        size="sm"
        variant="outline"
        onClick={doUnflag}
        disabled={busy}
        className="border-amber-300 text-amber-800 hover:bg-amber-50"
        data-testid={testId || `unflag-${resource}-button`}
      >
        <FlagOff className="w-4 h-4 mr-1.5" /> Unflag
      </Button>
    );
  }

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        className="border-amber-300 text-amber-800 hover:bg-amber-50"
        data-testid={testId || `flag-${resource}-button`}
      >
        <Flag className="w-4 h-4 mr-1.5" /> Flag
      </Button>
      <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Flag this {resource.slice(0, -1)}</DialogTitle>
            <DialogDescription>
              Add a short note describing what needs follow-up. The record stays in the workflow — it just appears in the Flagged queue for the team to resolve.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Amount does not match the sale total; needs Accounts review."
            maxLength={2000}
            rows={4}
            data-testid={`flag-${resource}-note`}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={doFlag} disabled={busy || !note.trim()} data-testid={`flag-${resource}-submit`}>
              {busy ? "Flagging…" : "Flag"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Small chip shown on records that are currently flagged (row + detail).
 */
export function FlagBadge({ flag, testId }) {
  if (!flag?.flagged) return null;
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold text-amber-900 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5"
      title={flag.note}
      data-testid={testId || "flag-badge"}
    >
      <Flag className="w-3 h-3" /> Flagged
    </span>
  );
}
